import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Upload, Music, Trash2, Play, Pause, AlertCircle } from 'lucide-react';
import api from '@/services/api';
import userManager from '@/services/userManager';
import PageHeader from '@/components/PageHeader';

const CustomSounds = ({ t }) => {
  const navigate = useNavigate();
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [soundName, setSoundName] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, soundId: null, soundName: '' });

  useEffect(() => {
    loadSounds();
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, []);

  const loadSounds = async () => {
    try {
      setLoading(true);
      const userId = await userManager.getUserId();
      const customSounds = await api.getCustomSounds(userId);
      setSounds(customSounds);
    } catch (error) {
      console.error('Error loading sounds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file extension (lossless formats only)
    const allowedExtensions = ['.wav', '.flac', '.m4a', '.aiff', '.aif', '.ape', '.wv', '.dsf', '.dff'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setUploadError('Format de fichier invalide. Formats sans perte acceptés: WAV, FLAC, ALAC (.m4a), AIFF, APE, WavPack (.wv), DSD (.dsf, .dff)');
      return;
    }

    // Validate file size (50 MB max for lossless formats)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Fichier trop volumineux. Taille maximale : 50 MB.');
      return;
    }

    setSelectedFile(file);
    setSoundName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!selectedFile || !soundName.trim()) {
      setUploadError(t.nameRequired || 'Veuillez donner un nom à votre son.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      const userId = await userManager.getUserId();
      
      await api.uploadCustomSound(userId, soundName.trim(), selectedFile);
      
      // Reset form
      setSelectedFile(null);
      setSoundName('');
      document.getElementById('file-input').value = '';
      
      // Reload sounds
      await loadSounds();
    } catch (error) {
      console.error('Error uploading sound:', error);
      setUploadError(error.message || t.uploadError || 'Erreur lors de l\'upload. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (soundId, soundName) => {
    setDeleteConfirmModal({ show: true, soundId, soundName });
  };

  const handleDeleteConfirm = async () => {
    const { soundId } = deleteConfirmModal;
    
    // Close modal first
    setDeleteConfirmModal({ show: false, soundId: null, soundName: '' });

    try {
      await api.deleteCustomSound(soundId);
      await loadSounds();
      
      // Stop playing if it's the deleted sound
      if (playingId === soundId) {
        setPlayingId(null);
        if (audioElement) {
          audioElement.pause();
        }
      }
    } catch (error) {
      console.error('❌ Error deleting sound:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      alert(t.deleteError || 'Erreur lors de la suppression.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ show: false, soundId: null, soundName: '' });
  };

  const handlePlayPause = (sound) => {
    if (playingId === sound.id) {
      // Pause current
      audioElement.pause();
      setPlayingId(null);
    } else {
      // Stop previous if any
      if (audioElement) {
        audioElement.pause();
      }

      // Play new
      const audio = new Audio(api.getCustomSoundStreamUrl(sound.id));
      audio.addEventListener('ended', () => setPlayingId(null));
      audio.play();
      setAudioElement(audio);
      setPlayingId(sound.id);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8 pb-40">
      <div className="max-w-4xl mx-auto">
        <PageHeader t={t} />

        <Card className="mb-6 bg-white rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-6 w-6" />
              {t.customSoundsTitle || 'Sonothèque Personnalisée'}
            </CardTitle>
            <CardDescription>
              {t.customSoundsDesc || 'Uploadez vos propres sons apaisants pour les utiliser dans vos sessions'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Formats sans perte acceptés : WAV, FLAC, ALAC (.m4a), AIFF, APE, WavPack (.wv), DSD (.dsf, .dff). Taille max : 50 MB
              </AlertDescription>
            </Alert>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                id="file-input"
                type="file"
                accept=".wav,.flac,.m4a,.aiff,.aif,.ape,.wv,.dsf,.dff,audio/wav,audio/flac,audio/m4a,audio/aiff,audio/ape,audio/wavpack,audio/dsd"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {!selectedFile ? (
                <div>
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                  <p className="text-gray-600 mb-2">
                    {t.dragDropOrClick || 'Glissez-déposez ou cliquez pour sélectionner'}
                  </p>
                  <Button
                    onClick={() => document.getElementById('file-input').click()}
                    variant="outline"
                  >
                    {t.selectFile || 'Sélectionner un fichier'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-green-600 flex items-center justify-center gap-2">
                    <Music className="h-5 w-5" />
                    <span className="font-medium">{selectedFile.name}</span>
                    <span className="text-sm text-gray-500">({formatFileSize(selectedFile.size)})</span>
                  </div>
                  
                  <div className="max-w-md mx-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.soundName || 'Nom du son'}
                    </label>
                    <Input
                      value={soundName}
                      onChange={(e) => setSoundName(e.target.value)}
                      placeholder={t.enterSoundName || 'Entrez un nom...'}
                      className="mb-4"
                    />
                  </div>

                  {uploadError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{uploadError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-4 justify-center">
                    <Button
                      onClick={handleUpload}
                      disabled={uploading || !soundName.trim()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploading ? (t.uploading || 'Upload...') : (t.upload || 'Uploader')}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedFile(null);
                        setSoundName('');
                        setUploadError('');
                        document.getElementById('file-input').value = '';
                      }}
                      variant="outline"
                    >
                      {t.cancel || 'Annuler'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle>{t.mySounds || 'Mes Sons'}</CardTitle>
            <CardDescription>
              {t.mySoundsDesc || 'Gérez votre bibliothèque de sons personnalisés'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-gray-500">{t.loading || 'Chargement...'}</p>
            ) : sounds.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {t.noSounds || 'Aucun son personnalisé. Uploadez votre premier son !'}
              </p>
            ) : (
              <div className="space-y-3">
                {sounds.map((sound) => (
                  <div
                    key={sound.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Music className="h-5 w-5 text-indigo-600" />
                      <div className="flex-1">
                        <p className="font-medium">{sound.name}</p>
                        <p className="text-sm text-gray-500">
                          {sound.filename} • {formatFileSize(sound.file_size)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handlePlayPause(sound)}
                        variant="outline"
                        size="sm"
                      >
                        {playingId === sound.id ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(sound.id, sound.name)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Delete Confirmation Modal */}
        {deleteConfirmModal.show && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={handleDeleteCancel}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-red-100 p-2 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t.confirmDeleteTitle || 'Confirmer la suppression'}
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                {t.confirmDeleteMessage || 'Êtes-vous sûr de vouloir supprimer définitivement'}{' '}
                <span className="font-semibold text-gray-900">"{deleteConfirmModal.soundName}"</span> ?
              </p>
              
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={handleDeleteCancel}
                  variant="outline"
                >
                  {t.cancel || 'Annuler'}
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t.delete || 'Supprimer'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSounds;